import type { CSSProperties, ReactNode } from "react";
import { PANELS } from "@/admin/catalog";
import { RiskBadge } from "@/components/admin/RiskBadge";
import { useFocusHighlight } from "./useFocusHighlight";

const SECTION: CSSProperties = { display: "flex", flexDirection: "column", gap: 12 };
const H2: CSSProperties = { fontFamily: "var(--font-display)", fontSize: 22, margin: 0 };
const LIST: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 8, margin: 0, padding: 0, listStyle: "none",
};
const ITEM: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 2, padding: "8px 12px",
  border: "1px solid var(--d-line)", borderRadius: 12, scrollMarginTop: 80,
};
const NAME: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8,
  fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 13, color: "var(--d-fg)",
};
const DESC: CSSProperties = {
  fontFamily: "var(--font-body)", fontSize: 12, color: "var(--d-fg-muted)",
};

interface Props {
  title: string;
  /** Catalog panel ids this section covers; their names and descriptions are listed above the page. */
  panelIds: readonly string[];
  children?: ReactNode;
}

/** Existing panel page embedded as a section; title and per-panel text come from `panels.ts`. */
export function PanelSection({ title, panelIds, children }: Props): JSX.Element {
  useFocusHighlight();
  const panels = panelIds.flatMap((id) => PANELS.filter((p) => p.id === id));
  return (
    <section style={SECTION}>
      <h2 style={H2}>{title}</h2>
      <ul style={LIST}>
        {panels.map((p) => (
          <li key={p.id} id={`panel-${p.id}`} data-panel-id={p.id} style={ITEM}>
            <span style={NAME}>
              {p.name}
              {p.risk ? <RiskBadge risk={p.risk} /> : null}
            </span>
            <span style={DESC}>{p.description}</span>
          </li>
        ))}
      </ul>
      {children}
    </section>
  );
}
